import axios from 'axios';

const apiUrl = process.env.REACT_APP_API_URL;

const adminApi = axios.create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getIdField = (endpoint) => {
  const idFields = {
    Investor: 'investorId',
    Property: 'propertyId',
    PropertyType: 'propertyTypeId',
    Address: 'addressId',
    Image: 'imageId',
    Tag: 'tagId',
    Blog: 'blogId',
    BlogCategory: 'categoryId',
    Question: 'questionId',
    Answer: 'answerId',
    Country: 'countryId',
    Currency: 'currencyId',
    Forest: 'forestId',
    Tree: 'treeId',
    Transaction: 'transactionId',
  };
  return idFields[endpoint] || 'id';
};

const formatError = (error, endpoint, operation) => {
  const status = error.response?.status || 'Network Error';
  const statusText = error.response?.statusText || '';
  const message = error.response?.data?.message || error.response?.data?.status?.message || error.message;
  const url = error.config?.url || `/${endpoint}`;
  const method = error.config?.method?.toUpperCase() || operation;
  
  console.error(`API Error [${endpoint}]:`, {
    operation,
    status,
    statusText,
    message,
    url,
    method,
    responseData: error.response?.data,
  });
  
  const enhancedError = new Error(
    `${operation} failed for ${endpoint}: ${status} ${statusText} - ${message}`
  );
  enhancedError.originalError = error;
  enhancedError.status = status;
  enhancedError.endpoint = endpoint;
  enhancedError.operation = operation;
  return enhancedError;
};

const createCrudService = (endpoint) => ({
  endpoint,
  
  getAll: async (params = {}) => {
    try {
      const { start = 0, pageSize = 50 } = params;
      const response = await adminApi.get(`/${endpoint}?start=${start}&pageSize=${pageSize}`);
      return response.data;
    } catch (error) {
      throw formatError(error, endpoint, 'GET_ALL');
    }
  },

  getById: async (id) => {
    try {
      const response = await adminApi.get(`/${endpoint}/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, endpoint, 'GET_BY_ID');
    }
  },

  create: async (data) => {
    try {
      const response = await adminApi.post(`/${endpoint}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, endpoint, 'CREATE');
    }
  },

  update: async (id, data) => {
    try {
      const response = await adminApi.put(`/${endpoint}/${id}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, endpoint, 'UPDATE');
    }
  },

  delete: async (id) => {
    try {
      const response = await adminApi.delete(`/${endpoint}/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, endpoint, 'DELETE');
    }
  },
});

export const addressesApi = createCrudService('Address');
export const addressesTrackerApi = createCrudService('AddressTracker');
export const answerApi = createCrudService('Answer');
export const answerInvestorApi = {
  endpoint: 'AnswerInvestor',
  
  getAll: async (params = {}) => {
    try {
      const response = await adminApi.get('/AnswerInvestor');
      return response.data;
    } catch (error) {
      throw formatError(error, 'AnswerInvestor', 'GET_ALL');
    }
  },

  getByInvestor: async (investorId) => {
    try {
      const response = await adminApi.get(`/AnswerInvestor/investor/${investorId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'AnswerInvestor', 'GET_BY_INVESTOR');
    }
  },

  delete: async (investorId, answerId) => {
    try {
      const response = await adminApi.delete(`/AnswerInvestor/${investorId}/${answerId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'AnswerInvestor', 'DELETE');
    }
  },
};
export const blogApi = createCrudService('Blog');
export const blogCategoryApi = createCrudService('BlogCategory');
export const cashWalletApi = {
  endpoint: 'CashWallet',
  
  getAll: async (params = {}) => {
    try {
      const response = await adminApi.get('/CashWallet');
      return response.data;
    } catch (error) {
      throw formatError(error, 'CashWallet', 'GET_ALL');
    }
  },

  getById: async (id) => {
    try {
      const response = await adminApi.get(`/CashWallet/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'CashWallet', 'GET_BY_ID');
    }
  },

  getByInvestor: async (investorId) => {
    try {
      const response = await adminApi.get(`/CashWallet/investor/${investorId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'CashWallet', 'GET_BY_INVESTOR');
    }
  },

  getBalance: async (investorId) => {
    try {
      const response = await adminApi.get(`/CashWallet/balance/${investorId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'CashWallet', 'GET_BALANCE');
    }
  },

  create: async (data) => {
    try {
      const response = await adminApi.post('/CashWallet/Create', data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'CashWallet', 'CREATE');
    }
  },

  adjust: async (investorId, data) => {
    try {
      const response = await adminApi.post(`/CashWallet/Adjust/${investorId}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'CashWallet', 'ADJUST');
    }
  },
};
export const contactUsApi = createCrudService('ContactUs');
export const countryApi = createCrudService('Country');
export const currencyApi = createCrudService('Currency');
export const earnXpApi = createCrudService('EarnXp');
export const favoriteApi = createCrudService('Favorite');
export const forSaleApi = {
  endpoint: 'ForSale',
  
  getAll: async (params = {}) => {
    try {
      const response = await adminApi.get('/ForSale');
      return response.data;
    } catch (error) {
      throw formatError(error, 'ForSale', 'GET_ALL');
    }
  },

  getById: async (id) => {
    try {
      const response = await adminApi.get(`/ForSale/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'ForSale', 'GET_BY_ID');
    }
  },

  create: async (data) => {
    try {
      const response = await adminApi.post('/ForSale/Create', data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'ForSale', 'CREATE');
    }
  },
};
export const forestApi = createCrudService('Forest');
export const imagesApi = createCrudService('Image');
export const investorApi = {
  endpoint: 'admin/investors',
  
  getAll: async (params = {}) => {
    try {
      const { page = 1, pageSize = 50, search = '' } = params;
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const response = await adminApi.get(`/admin/investors?page=${page}&pageSize=${pageSize}${searchParam}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Investor', 'GET_ALL');
    }
  },

  getById: async (id) => {
    try {
      const response = await adminApi.get(`/admin/investors/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Investor', 'GET_BY_ID');
    }
  },

  update: async (id, data) => {
    try {
      const response = await adminApi.put(`/admin/investors/${id}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Investor', 'UPDATE');
    }
  },

  delete: async (id) => {
    try {
      const response = await adminApi.delete(`/admin/investors/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Investor', 'DELETE');
    }
  },

  resetPassword: async (investorId) => {
    try {
      const response = await adminApi.post(`/admin/investors/${investorId}/reset-password`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Investor', 'RESET_PASSWORD');
    }
  },
};
export const investorEarnxpApi = createCrudService('InvestorEarnxp');
export const investorPropertiesApi = createCrudService('InvestorProperties');
export const investorXpApi = createCrudService('InvestorXp');
export const ipLoggingApi = createCrudService('IpLogging');
export const propertiesApi = createCrudService('Property');
export const propertiesTagsApi = createCrudService('PropertyTag');
export const propertiesTypeApi = createCrudService('PropertyType');
export const pruApi = createCrudService('Pru');
export const sectionApi = createCrudService('Section');
export const purchaseTransactionsApi = {
  endpoint: 'Transaction',
  
  getAll: async (params = {}) => {
    try {
      const { start = 0, pageSize = 50 } = params;
      const response = await adminApi.get(`/Transaction/List?start=${start}&pageSize=${pageSize}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Transaction', 'GET_ALL');
    }
  },

  getById: async (id) => {
    try {
      const response = await adminApi.get(`/Transaction/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Transaction', 'GET_BY_ID');
    }
  },

  create: async (data) => {
    try {
      const response = await adminApi.post('/Transaction/AddPurchaseTransaction', data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Transaction', 'CREATE');
    }
  },

  update: async (id, data) => {
    try {
      const response = await adminApi.put(`/Transaction/${id}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Transaction', 'UPDATE');
    }
  },

  delete: async (id) => {
    try {
      const response = await adminApi.delete(`/Transaction/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Transaction', 'DELETE');
    }
  },
};
export const questionApi = createCrudService('Question');
export const tagsApi = createCrudService('Tag');
export const tannitIdsApi = createCrudService('TannitIds');
export const topupTransactionApi = {
  endpoint: 'TopupTransaction',
  
  getAll: async (params = {}) => {
    try {
      const { start = 0, pageSize = 50 } = params;
      const response = await adminApi.get(`/Transaction/Topup?start=${start}&pageSize=${pageSize}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'TopupTransaction', 'GET_ALL');
    }
  },

  getById: async (id) => {
    try {
      const response = await adminApi.get(`/Transaction/Topup/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'TopupTransaction', 'GET_BY_ID');
    }
  },

  create: async (data) => {
    try {
      const response = await adminApi.post('/Transaction/AddTopupTransaction', data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'TopupTransaction', 'CREATE');
    }
  },

  update: async (id, data) => {
    try {
      const response = await adminApi.put(`/Transaction/Topup/${id}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'TopupTransaction', 'UPDATE');
    }
  },

  delete: async (id) => {
    try {
      const response = await adminApi.delete(`/Transaction/Topup/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'TopupTransaction', 'DELETE');
    }
  },
};
export const treeApi = createCrudService('Tree');
export const withdrawalTransactionApi = {
  endpoint: 'WithdrawalTransaction',
  
  getAll: async (params = {}) => {
    try {
      const response = await adminApi.get('/Transaction/Withdrawal');
      return response.data;
    } catch (error) {
      throw formatError(error, 'WithdrawalTransaction', 'GET_ALL');
    }
  },

  getById: async (id) => {
    try {
      const response = await adminApi.get(`/Transaction/Withdrawal/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'WithdrawalTransaction', 'GET_BY_ID');
    }
  },

  create: async (data) => {
    try {
      const response = await adminApi.post('/Transaction/AddWithdrawalTransaction', data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'WithdrawalTransaction', 'CREATE');
    }
  },
};

export default adminApi;
